import { FC, useEffect, useRef } from "react"
import { Grid } from "@mui/material"
import {
  AutocompleteElement,
  DatePickerElement,
  FormContainer,
  useForm,
} from "react-hook-form-mui"
import { useWatch } from "react-hook-form"
import equal from "fast-deep-equal"
import { format } from "date-fns"
import { useAppDispatch, useAppSelector } from "@/modules/app/hooks"
import { ListboxComponent } from "@core/components"
import { useUrlFilters, UrlFilterSchema } from "@core/hooks"
import {
  getRealizationState,
  setBatchesFilters,
  useRealizationsFiltersQuery,
} from "../../store"
import { Realization } from "../../interfaces"
import { cleanFilters, parseDateParam } from "./filterUrl"

const FILTER_SCHEMA = {
  categories: "numberArray",
  products: "numberArray",
  couriers: "numberArray",
  cities: "numberArray",
  start: "string",
  end: "string",
} as const satisfies UrlFilterSchema

interface IFormData {
  categories: Realization.Category[]
  products: Realization.Product[]
  couriers: Realization.IdName[]
  cities: Realization.IdName[]
  start: Date | null
  end: Date | null
}

interface IProps {}

const toQueryParams = (
  data: Partial<IFormData>,
): Realization.BatchesQueryParams => ({
  categories: data.categories?.map((c) => c.id),
  products: data.products?.map((p) => p.id),
  couriers: data.couriers?.map((c) => c.id),
  cities: data.cities?.map((c) => c.id),
  start: data.start ? format(data.start, "yyyy-MM-dd") : undefined,
  end: data.end ? format(data.end, "yyyy-MM-dd") : undefined,
})

export const BatchFilters: FC<IProps> = () => {
  const dispatch = useAppDispatch()
  const { batchesFilters } = useAppSelector(getRealizationState)
  const { data: filters, isLoading } = useRealizationsFiltersQuery({})
  const { read, write, hasUrlFilters } = useUrlFilters(FILTER_SCHEMA)
  const didRestore = useRef(false)
  const lastSyncedRef = useRef<Realization.BatchesQueryParams | null>(null)

  const formContext = useForm<IFormData>({
    defaultValues: {
      categories: [],
      products: [],
      couriers: [],
      cities: [],
      start: null,
      end: null,
    },
  })

  const watch = useWatch({ control: formContext.control }) as IFormData

  // Restore filters from the URL once the option lists are loaded so we can
  // map the ids back to the full option objects the form expects.
  useEffect(() => {
    if (didRestore.current || isLoading || !filters) return
    didRestore.current = true
    if (!hasUrlFilters) return

    const url = read()

    const restored: IFormData = {
      categories: filters.categories.filter((c) =>
        url.categories?.includes(c.id),
      ),
      products: filters.products.filter((p) => url.products?.includes(p.id)),
      couriers: filters.couriers.filter((c) => url.couriers?.includes(c.id)),
      cities: filters.cities.filter((c) => url.cities?.includes(c.id)),
      start: null,
      end: null,
    }

    formContext.reset(restored)
    dispatch(setBatchesFilters(cleanFilters(toQueryParams(restored))))
  }, [filters, isLoading])

  useEffect(() => {
    if (!formContext.formState.isDirty) return

    const cleaned = cleanFilters(toQueryParams(watch))

    // Skip when the serialized filters haven't changed to avoid re-writing the
    // URL / dispatching on every render.
    if (equal(cleaned, lastSyncedRef.current)) return
    lastSyncedRef.current = cleaned

    // Reflect the active filters in the URL so the link can be shared.
    write(cleaned)

    if (equal(cleaned, batchesFilters)) return
    dispatch(setBatchesFilters(cleaned))
  }, [watch])

  const autocompleteShared = {
    fullWidth: true,
    ListboxComponent: ListboxComponent,
  }

  return (
    <FormContainer formContext={formContext}>
      <Grid container spacing={1} alignItems="center" my={1}>
        <Grid item xs={12} sm={6} md={4} xl={2}>
          <AutocompleteElement
            name="categories"
            label="Категория"
            loading={isLoading}
            multiple
            showCheckbox
            options={filters?.categories ?? []}
            autocompleteProps={{
              ...autocompleteShared,
              getOptionLabel: (o: Realization.Category) => o.name,
              isOptionEqualToValue: (o, v) => o.id === v.id,
              renderOption: (props, option, state) =>
                [props, option.name, state] as React.ReactNode,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} xl={2}>
          <AutocompleteElement
            name="products"
            label="Продукт"
            loading={isLoading}
            multiple
            showCheckbox
            options={filters?.products ?? []}
            autocompleteProps={{
              ...autocompleteShared,
              getOptionLabel: (o: Realization.Product) => o.name,
              isOptionEqualToValue: (o, v) => o.id === v.id,
              renderOption: (props, option, state) =>
                [props, option.name, state] as React.ReactNode,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} xl={2}>
          <AutocompleteElement
            name="couriers"
            label="Курьер"
            loading={isLoading}
            multiple
            showCheckbox
            options={filters?.couriers ?? []}
            autocompleteProps={{
              ...autocompleteShared,
              getOptionLabel: (o: Realization.IdName) => o.name,
              isOptionEqualToValue: (o, v) => o.id === v.id,
              renderOption: (props, option, state) =>
                [props, option.name, state] as React.ReactNode,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} xl={2}>
          <AutocompleteElement
            name="cities"
            label="Город"
            loading={isLoading}
            multiple
            showCheckbox
            options={filters?.cities ?? []}
            autocompleteProps={{
              ...autocompleteShared,
              getOptionLabel: (o: Realization.IdName) => o.name,
              isOptionEqualToValue: (o, v) => o.id === v.id,
              renderOption: (props, option, state) =>
                [props, option.name, state] as React.ReactNode,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} xl={2}>
          <DatePickerElement
            name="start"
            label="Период с"
            inputProps={{ fullWidth: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} xl={2}>
          <DatePickerElement
            name="end"
            label="По"
            inputProps={{ fullWidth: true }}
          />
        </Grid>
      </Grid>
    </FormContainer>
  )
}
